import React, { useMemo } from "react";
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  AccessTimeFilled as AccessTimeFilledIcon,
} from "@mui/icons-material";
import placeholderIllustration from "@/assets/ai-answers.svg";
import {
  Avatar,
  Divider,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@ui";
import { CloseIcon } from "@ui";
import type { Company } from "@/pages/private/dashboard/SharePage";
import { ThemeColours } from "@/clients/acme/config/colours";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { sharePageEndpoints } from "@/shared/api/endpoints";
import { httpPut } from "@/shared/api/http";
import { CustomerSharingResponse } from "@/shared/api/types";
import { useAlert } from "@/app/providers/AlertProvider";

interface ShareHistoryDialogProps {
  onClose: () => void;
  companies: Company[];
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  shareBudgetsLogic: () => boolean;
}

const ShareHistoryDialog: React.FC<ShareHistoryDialogProps> = ({
  onClose,
  companies,
  setCompanies,
  shareBudgetsLogic,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showAlert } = useAlert();

  const isShareHistoryEmpty = useMemo(() => {
    let flag = true;

    for (const company of companies) {
      if (!(!company.raw?.lastSharedDate)) {
        flag = false;
      }
    }

    return flag;
  }, [companies]);

  const shareAgain = async (company: Company) => {
    const disableShare = company.disableShare || false;

    // if (shareDate && isWithin24Hours(shareDate) && !canShareAgain) {
    //   // showAlert({
    //   //   severity: "warning",
    //   //   message: `You can't share the same IE with companies that you've already shared it with in the past 24 hours.`,
    //   //   autoHide: true,
    //   //   duration: 4000,
    //   // });

    //   disableShare = true;
    // }

    if (company.isFp) {
      await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createFreepostSharing(),
        {
          ...company.raw,
          isSharing: "Y",
          shareNow: disableShare ? "N" : "Y",
        }
      );
    } else {
      await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerSharing(),
        {
          ...company.raw,
          isSharing: "Y",
          shareNow: disableShare ? "N" : "Y",
        }
      );
    }

    setCompanies((prev) =>
      prev.map((c) => {
        if (c.id == company.id) {
          c.raw.isSharing = "Y";
          c.raw.shareNow = disableShare ? "N" : "Y";
        }

        return c;
      })
    );

    onClose();
  };

  const renderSharedCompanyCard = (company: Company) => {
    if (!company.raw?.lastSharedDate) {
      return;
    }

    return (
      <Paper
        key={company.id}
        elevation={0}
        sx={{
          p: isMobile ? 2 : 3,
          mb: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 0 }}>
          <Avatar
            sx={{
              bgcolor: "#DBEAFE",
              color: theme.palette.primary.contrastText,
              width: isMobile ? 45 : 52,
              height: isMobile ? 45 : 52,
              borderRadius: 2,
            }}
          >
            <AccountBalanceIcon
              sx={{ fontSize: isMobile ? 22 : 26, color: "#2562EB" }}
            />
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box>
                <Typography
                  variant={isMobile ? "body1" : "h5"}
                  sx={{ fontWeight: 400, color: "#0B3D3A", mb: 0 }}
                >
                  {company.name}
                </Typography>
                <Typography
                  data-private
                  variant="h6"
                  sx={{ color: "#ADB5BD", fontWeight: 400 }}
                >
                  {company.reference}
                </Typography>
              </Box>
            </Box>

            {company.address && (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 1 }}
              >
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeFilledIcon sx={{ fontSize: 16, color: "#6B727F" }} />
            <Typography variant="body2" sx={{ color: "#6B727F" }}>
              Last shared on{" "}
              {company.raw?.submitDate || company.raw?.lastSharedDate
                ? formatDate(
                    (company.raw?.submitDate as string) ||
                      (company.raw?.lastSharedDate as string)
                  )
                : "-"}
            </Typography>
          </Box>

          {company.raw?.isSharing == "N" ? (
            <Button
              variant="text"
              sx={{
                color: ThemeColours.azul,
                textTransform: "none",
                fontWeight: 400,
                backgroundColor: "rgba(37, 97, 235, 0.08)",
                py: 1,
                px: 2,
                "&:hover": {
                  backgroundColor: "rgba(37, 97, 235, 0.08)",
                },
              }}
              onClick={() => shareAgain(company)}
            >
              Share Again
            </Button>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: ThemeColours.subtitleVar2 }}
            >
              Added
            </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Dialog Title */}
      <DialogTitle
        sx={{
          px: 4,
          justifyContent: "space-between",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Share History
        </Typography>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ mx: 4 }} />

      {/* Dialog Content */}
      <DialogContent sx={{ pt: 3, px: 4, flexGrow: 1, overflowY: "auto" }}>
        {isShareHistoryEmpty && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height={isMobile ? "auto" : "100%"}
            textAlign="center"
          >
            <img
              className="placeholder-illustration"
              alt="Placeholder Illustration"
              src={placeholderIllustration}
              style={{ marginBottom: 16, height: 150 }}
            />

            <Typography
              sx={{
                color: "#013B3E",
                fontWeight: 700,
                fontSize: 24,
                lineHeight: 1,
              }}
            >
              You haven’t shared with any companies.
            </Typography>
          </Box>
        )}

        {companies.map(renderSharedCompanyCard)}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 2,
          borderTop: "1px solid #e0e0e0",
          position: "sticky",
          bottom: 0,
          backgroundColor: "white",
          zIndex: 1,
          boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            height: "45px",
            backgroundColor: "#f3f4f6",
            borderRadius: "10px",
            color: "#1f2937",
            fontSize: "18px",
            fontWeight: 600,
            textTransform: "none",
            border: "none",
            "&:hover": {
              backgroundColor: "#e5e7eb",
              border: "none",
            },
            "&:disabled": {
              backgroundColor: "#f3f4f6",
              color: "#9ca3af",
            },
            minWidth: 100,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Box>
  );
};

export default ShareHistoryDialog;
